"use client";

import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { matchBandColor } from "../../app/lib/resume/matchScore";

function keywordLabel(item) {
  return String(item?.keyword || item?.keyword_normalized || "").trim();
}

function keywordLabels(items, limit = 12) {
  const seen = new Set();
  const labels = [];
  for (const item of items || []) {
    const label = keywordLabel(item);
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    labels.push(label);
    if (labels.length >= limit) break;
  }
  const totalUnique = (() => {
    const all = new Set();
    for (const item of items || []) {
      const label = keywordLabel(item);
      if (label) all.add(label.toLowerCase());
    }
    return all.size;
  })();
  if (totalUnique > labels.length) {
    labels.push(`+${totalUnique - labels.length} more`);
  }
  return labels;
}

function KeywordBlock({ title, titleClassName, items }) {
  const labels = keywordLabels(items);
  return (
    <div className="min-w-0 w-full">
      <div className={`font-medium mb-0.5 ${titleClassName}`}>{title}</div>
      {labels.length === 0 ? (
        <div className="text-gray-400">—</div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {labels.map((label, index) => (
            <span
              key={`${index}-${label}`}
              className="inline-block max-w-full break-words [overflow-wrap:anywhere] rounded bg-[#f3f1ed] px-1.5 py-0.5 text-[10px] leading-snug text-[#5f5e59]"
            >
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ScoreHeader({ current, boost }) {
  const gain = Math.max(0, boost - current);
  return (
    <div className="min-w-0 w-full">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[10px] uppercase tracking-wide text-[#8b8a86]">ATS Match</span>
      </div>
      <div className="mt-0.5 flex items-baseline gap-1.5 flex-wrap">
        <span className="text-sm font-semibold tabular-nums text-[#27251f]">{current}%</span>
        {gain > 0 ? (
          <>
            <span className="text-[#c4c0b8]" aria-hidden>
              →
            </span>
            <span className="text-sm font-semibold tabular-nums text-[#507619]">{boost}%</span>
            <span className="rounded-full bg-[#eef4e4] px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-[#507619]">
              +{gain}% after boost
            </span>
          </>
        ) : (
          <span className="text-[10px] text-[#8b8a86]">already at boost ceiling</span>
        )}
      </div>
    </div>
  );
}

/**
 * Fixed portal tooltip — table overflow-x-auto clips absolute tooltips to the right.
 */
function PortalTooltip({ anchorRef, open, children, wide = false }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) return;
    const update = () => {
      const r = anchorRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 8, left: r.left + r.width / 2 });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchorRef]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      role="tooltip"
      className={`
        pointer-events-none fixed z-[200]
        -translate-x-1/2
        bg-white text-gray-700 text-[11px] leading-snug
        rounded px-2.5 py-2 shadow-md border border-[#e4e2dd]
        whitespace-normal break-words [overflow-wrap:anywhere]
        text-left
        ${wide ? "w-[min(280px,70vw)] max-w-[280px] flex flex-col gap-2" : "max-w-[220px] w-max flex flex-col gap-0.5"}
      `}
      style={{ top: pos.top, left: pos.left }}
    >
      {children}
    </div>,
    document.body,
  );
}

/**
 * Circular ATS match gauge: solid current arc + soft boost ceiling arc.
 * Ceiling = soft_skill | responsibility | domain only (dictionary-backed).
 */
export default function MatchScoreRing({
  currentPercent = null,
  boostPercent = null,
  band = "weak",
  size = 44,
  emptyLabel = "—",
  emptyHint = "Upload a resume to see ATS Match",
  debug = false,
  matched = [],
  boostable = [],
  missingHard = [],
}) {
  // Flip to true (with ?atsDebug=1) when iterating on boostable vs hard gaps.
  const showGapDebug = false;
  const hasScore = currentPercent != null && Number.isFinite(currentPercent);
  const colors = matchBandColor(band);
  const stroke = 4;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const current = hasScore ? Math.max(0, Math.min(100, currentPercent)) : 0;
  const boost = hasScore && boostPercent != null ? Math.max(current, Math.min(100, boostPercent)) : current;
  const gain = Math.max(0, boost - current);

  const currentLen = (current / 100) * circumference;
  const boostLen = (boost / 100) * circumference;

  const ariaLabel = hasScore
    ? gain > 0
      ? `ATS Match ${current}%, after boost ${boost}%`
      : `ATS Match ${current}%`
    : emptyHint;

  const tooltipLines = useMemo(() => {
    if (!hasScore) return [emptyHint];
    if (gain > 0) {
      return [`ATS Match ${current}% → ${boost}% after boost`, "Rephrase soft/domain keywords only — no new tech"];
    }
    return [`ATS Match ${current}%`, "Already at boost ceiling"];
  }, [hasScore, current, boost, gain, emptyHint]);

  const show = useCallback(() => setOpen(true), []);
  const hide = useCallback(() => setOpen(false), []);

  return (
    <span
      ref={anchorRef}
      className="relative inline-flex items-center justify-center cursor-default"
      style={{ width: size, height: size }}
      aria-label={ariaLabel}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <svg width={size} height={size} className="block -rotate-90" aria-hidden>
        <circle cx={center} cy={center} r={radius} fill="none" stroke="#e4e2dd" strokeWidth={stroke} />
        {hasScore && boost > current && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={colors.soft}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${boostLen} ${circumference - boostLen}`}
          />
        )}
        {hasScore && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={colors.solid}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${currentLen} ${circumference - currentLen}`}
          />
        )}
      </svg>
      <span
        className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold tabular-nums leading-none"
        style={{ color: hasScore ? colors.solid : "#8b8a86" }}
      >
        {hasScore ? `${current}` : emptyLabel}
      </span>

      <PortalTooltip anchorRef={anchorRef} open={open} wide={hasScore}>
        {!hasScore ? (
          tooltipLines.map((line, i) => (
            <span key={i} className="whitespace-normal break-words [overflow-wrap:anywhere]">
              {i === 0 ? <span className="font-medium text-[#27251f]">{line}</span> : line}
            </span>
          ))
        ) : (
          <>
            <ScoreHeader current={current} boost={boost} />
            <KeywordBlock title="Matched" titleClassName="text-[#507619]" items={matched} />
            {showGapDebug && debug ? (
              <>
                <KeywordBlock title="Boostable" titleClassName="text-[#c47a12]" items={boostable} />
                <KeywordBlock title="Missing (won't add)" titleClassName="text-[#c0392b]" items={missingHard} />
              </>
            ) : null}
          </>
        )}
      </PortalTooltip>
    </span>
  );
}
